<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import Modal from "$lib/components/Modal.svelte";
  import { api } from "$lib/api/client";
  import { auth } from "$lib/stores/auth.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { navigate } from "$lib/router.svelte";
  import { onMount } from "svelte";

  interface Plan {
    id: string;
    name: string;
    price_brl_month: number;
    price_brl_year: number;
    members: number | null;
    tx_per_month: number | null;
    storage_gb: number;
    rules: number | null;
    highlights: string[];
  }

  interface PixInfo {
    payload: string;
    qr_base64: string;
    amount: number;
    key: string;
    key_type: string;
    merchant: string;
    bank: string | null;
    agency: string | null;
    account: string | null;
    payee_doc: string | null;
    txid: string;
  }

  interface CheckoutOut {
    intent_id: string;
    plan: string;
    cycle: string;
    status: string;
    expires_at: string;
    pix: PixInfo | null;
    message: string;
  }

  let plans = $state<Plan[]>([]);
  let cycle = $state<"month" | "year">("month");
  let loading = $state(true);
  let processing = $state<string | null>(null);

  let pixOpen = $state(false);
  let checkout_ = $state<CheckoutOut | null>(null);
  let pollTimer = $state<ReturnType<typeof setInterval> | null>(null);
  let paymentConfirmed = $state(false);

  let currentPlan = $derived(auth.workspaces[0]?.plan ?? "free");

  onMount(async () => {
    try { plans = await api<Plan[]>("/billing/plans"); }
    finally { loading = false; }
  });

  function fmt(n: number): string {
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  function startPolling(intentId: string) {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(async () => {
      try {
        const r = await api<CheckoutOut>(`/billing/intent/${intentId}`);
        if (r.status === "paid") {
          clearInterval(pollTimer!);
          pollTimer = null;
          paymentConfirmed = true;
          await auth.bootstrap();
        }
      } catch { /* ignora erros de polling */ }
    }, 30_000);
  }

  async function checkout(plan: Plan) {
    if (plan.id === currentPlan) return;
    if (plan.id === "free") {
      toasts.info("Para reduzir de plano, fale com o suporte.");
      return;
    }
    processing = plan.id;
    try {
      const r = await api<CheckoutOut>("/billing/checkout", {
        method: "POST", body: JSON.stringify({ plan: plan.id, cycle }),
      });
      checkout_ = r;
      pixOpen = true;
      startPolling(r.intent_id);
    } catch (e: any) {
      toasts.error("Não foi possível iniciar o checkout", e.message);
    } finally { processing = null; }
  }

  function closeModal() {
    pixOpen = false;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (paymentConfirmed) navigate("/dashboard");
  }

  async function copyPayload() {
    if (!checkout_?.pix) return;
    try {
      await navigator.clipboard.writeText(checkout_.pix.payload);
      toasts.success("Copia-e-cola copiado");
    } catch {
      toasts.warn("Não foi possível copiar", "Selecione e copie manualmente.");
    }
  }
</script>

<Navbar />
<main class="max-w-6xl mx-auto px-4 py-8">
  <div class="text-center mb-8">
    <h1 class="text-3xl font-bold">Planos e preços</h1>
    <p class="text-muted mt-2">Comece grátis. Faça upgrade quando precisar.</p>

    <div class="inline-flex items-center gap-1 mt-5 p-1 rounded-full border" style="border-color: var(--border)">
      <button class="px-4 py-1.5 rounded-full text-sm font-medium transition"
              class:bg-brand={cycle === "month"} class:text-white={cycle === "month"}
              onclick={() => (cycle = "month")}>Mensal</button>
      <button class="px-4 py-1.5 rounded-full text-sm font-medium transition"
              class:bg-brand={cycle === "year"} class:text-white={cycle === "year"}
              onclick={() => (cycle = "year")}>
        Anual <span class="text-xs opacity-80">(2 meses grátis)</span>
      </button>
    </div>
  </div>

  {#if loading}
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      {#each [1, 2, 3] as _}<div class="skeleton h-80"></div>{/each}
    </div>
  {:else}
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      {#each plans as p}
        {@const price = cycle === "month" ? p.price_brl_month : p.price_brl_year}
        {@const isCurrent = currentPlan === p.id}
        {@const featured = p.id === "pro"}
        <div class="card relative flex flex-col" class:ring-2={featured}
             style={featured ? '--tw-ring-color: var(--brand)' : ''}>
          {#if featured}
            <div class="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-xs px-3 py-1 rounded-full">
              Mais popular
            </div>
          {/if}
          <h2 class="text-xl font-bold">{p.name}</h2>
          <div class="mt-2">
            <span class="text-3xl font-extrabold">{price === 0 ? "Grátis" : fmt(price)}</span>
            {#if price > 0}<span class="text-muted text-sm"> /{cycle === "month" ? "mês" : "ano"}</span>{/if}
          </div>
          <ul class="text-sm space-y-1.5 mt-4 flex-1">
            {#each p.highlights as h}
              <li class="flex gap-2"><span class="text-brand">✓</span>{h}</li>
            {/each}
          </ul>
          <button class="mt-5 {isCurrent ? 'btn-secondary' : 'btn-primary'} w-full"
                  disabled={isCurrent || processing === p.id}
                  onclick={() => checkout(p)}>
            {#if isCurrent}Plano atual
            {:else if processing === p.id}Processando…
            {:else if p.id === "free"}Voltar ao Free
            {:else}Assinar {p.name}{/if}
          </button>
        </div>
      {/each}
    </div>

    <p class="text-center text-xs text-muted mt-6">
      Pagamento via PIX (instantâneo) · Cancela quando quiser · Sem fidelidade
    </p>
  {/if}
</main>

<Modal bind:open={pixOpen} title="Pagamento via PIX" onClose={closeModal}>
  {#if paymentConfirmed}
    <div class="text-center space-y-3 py-6">
      <div class="text-5xl">🎉</div>
      <h2 class="text-xl font-bold text-green-400">Pagamento confirmado!</h2>
      <p class="text-muted text-sm">Seu plano está ativo. Redirecionando…</p>
    </div>
  {:else if checkout_?.pix}
    {@const x = checkout_.pix}
    <div class="space-y-4 text-sm">
      <div class="text-center">
        <div class="text-2xl font-bold text-brand">{fmt(x.amount)}</div>
        <div class="text-muted text-xs">Plano {checkout_!.plan.toUpperCase()} · {checkout_!.cycle === "year" ? "anual" : "mensal"}</div>
      </div>

      <div class="flex justify-center">
        <img src={x.qr_base64} alt="QR Code PIX" class="w-56 h-56 bg-white p-2 rounded" />
      </div>

      <div>
        <div class="label">PIX Copia-e-cola</div>
        <textarea readonly class="input font-mono text-xs" rows="3" value={x.payload}></textarea>
        <button class="btn-secondary w-full mt-2" onclick={copyPayload}>📋 Copiar código</button>
      </div>

      <div class="card text-xs space-y-1" style="background: var(--bg)">
        <div><strong>Beneficiário:</strong> {x.merchant}</div>
        {#if x.payee_doc}<div><strong>CNPJ:</strong> {x.payee_doc}</div>{/if}
        {#if x.bank}<div><strong>Banco:</strong> {x.bank}</div>{/if}
        {#if x.agency}<div><strong>Agência:</strong> {x.agency} · <strong>Conta:</strong> {x.account}</div>{/if}
        <div><strong>Chave PIX ({x.key_type.toUpperCase()}):</strong> {x.key}</div>
        <div><strong>Identificador:</strong> {x.txid}</div>
      </div>

      <div class="flex items-center gap-2 text-xs text-muted">
        <span class="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0"></span>
        Aguardando confirmação… (verificamos a cada 30 segundos)
      </div>

      <p class="text-xs text-muted">
        Após pagar, envie o comprovante para <strong>contato@takite.com</strong>
        com o código <strong>{x.txid}</strong> e seu plano é liberado em até 1h.
      </p>
    </div>
  {/if}
</Modal>
